import { useCallback, useEffect, useState } from 'react';
import type { CoreOrganization } from '@/lib/zifekCore';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuth } from '@/hooks/useAuth';
import {
  AI_TOOLS_FALLBACK,
  fetchAiToolStats,
  invokeAiTool,
  invokeAiToolChain,
  listAiToolCalls,
  listAiTools,
  setAiToolActive,
  toolCallStatusMeta,
  type AiPlanCondition,
  type AiPlanResult,
  type AiPlanStep,
  type AiToolCall,
  type AiToolDef,
  type AiToolResult,
} from '@/lib/coreAiTools';

interface PlanRow {
  tool: string;
  alias: string;
  paramsText: string;
  ifPrevious: AiPlanCondition;
}

const newPlanRow = (tool = 'search_customers', alias = '', paramsText = ''): PlanRow => ({
  tool, alias, paramsText, ifPrevious: 'always',
});

interface Props {
  commerceId: number;
  org: CoreOrganization;
}

const INPUT = 'w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors';

export default function AiGatewayTab({ commerceId, org }: Props) {
  const { user } = useAuth();
  const { has } = usePermissions();
  const [tools, setTools] = useState<AiToolDef[]>([]);
  const [calls, setCalls] = useState<AiToolCall[]>([]);
  const [stats, setStats] = useState<{ total: number; authorized: number; forbidden: number }>({ total: 0, authorized: 0, forbidden: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState<number | null>(null);

  const [selected, setSelected] = useState('search_customers');
  const [inputValue, setInputValue] = useState('');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<AiToolResult | null>(null);
  const [runError, setRunError] = useState('');

  // Mode function-calling multi-étapes
  const [mode, setMode] = useState<'single' | 'chain'>('single');
  const [planRows, setPlanRows] = useState<PlanRow[]>([
    { tool: 'search_customers', alias: 'customers', paramsText: '{"q":"Ahmed"}', ifPrevious: 'always' },
    { tool: 'get_customer_orders', alias: 'orders', paramsText: '{"customer_id":"{{customers.0.id}}"}', ifPrevious: 'has_results' },
    { tool: 'get_customer_bookings', alias: 'bookings', paramsText: '{"customer_id":"{{customers.0.id}}"}', ifPrevious: 'has_results' },
  ]);
  const [planRunning, setPlanRunning] = useState(false);
  const [planRun, setPlanRun] = useState<AiPlanResult | null>(null);
  const [planError, setPlanError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [t, c, s] = await Promise.all([
        listAiTools(),
        listAiToolCalls(org.id, 60),
        fetchAiToolStats(org.id),
      ]);
      setTools(t);
      setCalls(c);
      setStats(s);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [org.id]);

  useEffect(() => { load(); }, [load]);

  const toggleTool = async (t: AiToolDef) => {
    setBusyId(t.id);
    try { await setAiToolActive(t.id, !t.is_active); load(); } finally { setBusyId(null); }
  };

  const activeTools = tools.length > 0 ? tools : AI_TOOLS_FALLBACK.map((t, i) => ({ ...t, id: -(i + 1) }));
  const current = activeTools.find((t) => t.tool_key === selected);
  const needsCustomerId = selected.startsWith('get_customer_');
  const isWrite = current?.category === 'write';

  const run = async () => {
    setRunning(true);
    setResult(null);
    setRunError('');
    try {
      let params: Record<string, unknown>;
      if (isWrite) {
        if (inputValue.trim()) {
          try {
            params = JSON.parse(inputValue) as Record<string, unknown>;
          } catch {
            setRunError('Paramètres JSON invalides.');
            return;
          }
        } else {
          params = {};
        }
      } else {
        params = needsCustomerId ? { customer_id: Number(inputValue) || null } : { q: inputValue };
      }
      const res = await invokeAiTool(selected, params, {
        tenantId: org.tenant_id,
        organizationId: org.id,
        commerceId,
        userId: user?.id ?? null,
        canAccess: (p) => has(p),
        source: 'dashboard',
      });
      setResult(res);
      load();
    } finally {
      setRunning(false);
    }
  };

  const updatePlanRow = (idx: number, patch: Partial<PlanRow>) => {
    setPlanRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const runChain = async () => {
    setPlanRunning(true);
    setPlanError('');
    setPlanRun(null);
    try {
      const steps: AiPlanStep[] = planRows.map((r) => {
        let params: Record<string, unknown> = {};
        if (r.paramsText.trim()) {
          try {
            params = JSON.parse(r.paramsText) as Record<string, unknown>;
          } catch {
            throw new Error(`Paramètres JSON invalides pour « ${r.tool} ».`);
          }
        }
        return { tool: r.tool, alias: r.alias.trim() || undefined, params, ifPrevious: r.ifPrevious };
      });
      const res = await invokeAiToolChain(steps, {
        tenantId: org.tenant_id,
        organizationId: org.id,
        commerceId,
        userId: user?.id ?? null,
        canAccess: (p) => has(p),
        source: 'dashboard_chain',
      });
      setPlanRun(res);
      load();
    } catch (e) {
      setPlanError(e instanceof Error ? e.message : 'Erreur lors de l’enchaînement.');
    } finally {
      setPlanRunning(false);
    }
  };

  return (
    <div className="space-y-5">
      <p className="text-xs text-foreground-500 max-w-3xl">
        <span className="font-semibold text-foreground-700">AI Ready</span> : Zifek AI n’accède jamais directement à la base. Le Core expose des services
        internes utilisables par le <span className="font-semibold text-foreground-700">Zifek AI Tool Gateway</span>, avec authorization et validation à chaque appel.
      </p>

      {/* Flux */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          {['AI', 'tool()', 'Authorization', 'Zifek Core', 'Validated result'].map((step, i, arr) => (
            <span key={step} className="flex items-center gap-2">
              <span className={`px-3 py-1.5 rounded-full font-medium whitespace-nowrap ${i === 2 ? 'bg-accent-100 text-accent-900' : i === 3 ? 'bg-secondary-100 text-secondary-900' : 'bg-background-100 text-foreground-700'}`}>{step}</span>
              {i < arr.length - 1 && <i className="ri-arrow-right-line text-foreground-300"></i>}
            </span>
          ))}
        </div>
      </div>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}

      <div className="grid grid-cols-3 gap-2">
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-3">
          <i className="ri-robot-2-line text-lg text-primary-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.total}</p>
          <p className="text-xs text-foreground-500">Appels passerelle</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-3">
          <i className="ri-checkbox-circle-line text-lg text-secondary-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.authorized}</p>
          <p className="text-xs text-foreground-500">Autorisés</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-3">
          <i className="ri-shield-cross-line text-lg text-accent-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.forbidden}</p>
          <p className="text-xs text-foreground-500">Refusés</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
      ) : (
        <>
          {/* Registre des outils */}
          <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
              <span className="w-8 h-8 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center"><i className="ri-tools-line"></i></span>
              <h4 className="text-sm font-semibold text-foreground-900">Outils exposés à l’IA</h4>
              <span className="text-[11px] text-foreground-400 ml-auto">{activeTools.length} outil(s)</span>
            </div>
            <div className="divide-y divide-background-200/60">
              {activeTools.map((t) => (
                <div key={t.tool_key} className="flex items-center gap-3 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground-800 truncate flex items-center gap-2">
                      <code className="text-[12px] bg-background-100 px-1.5 py-0.5 rounded">{t.tool_key}</code>
                      <span className="text-foreground-400 font-normal">{t.label}</span>
                    </p>
                    <p className="text-[11px] text-foreground-400 truncate">
                      {t.required_permission ? `permission : ${t.required_permission}` : 'aucune permission'} · {t.returns || '—'}
                    </p>
                  </div>
                  {t.id > 0 && (
                    <button
                      onClick={() => toggleTool(t)}
                      disabled={busyId === t.id}
                      title={t.is_active ? 'Désactiver' : 'Activer'}
                      className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer flex-shrink-0 ${t.is_active ? 'bg-primary-500' : 'bg-background-200'}`}
                    >
                      <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-background-50 transition-all ${t.is_active ? 'left-[22px]' : 'left-0.5'}`}></span>
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Testeur */}
          <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-background-200/60 flex flex-wrap items-center gap-2">
              <span className="w-8 h-8 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center"><i className="ri-play-circle-line"></i></span>
              <h4 className="text-sm font-semibold text-foreground-900">Tester le Tool Gateway</h4>
              <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 ml-auto">
                <button onClick={() => setMode('single')} className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${mode === 'single' ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}>Appel simple</button>
                <button onClick={() => setMode('chain')} className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${mode === 'chain' ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}>Multi-étapes</button>
              </div>
            </div>
            <div className="p-4 space-y-3">
              {mode === 'single' && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label className="block">
                    <span className="block text-xs font-medium text-foreground-600 mb-1">Outil</span>
                    <select className={INPUT} value={selected} onChange={(e) => { setSelected(e.target.value); setResult(null); setInputValue(''); }}>
                      {activeTools.map((t) => <option key={t.tool_key} value={t.tool_key}>{t.tool_key}</option>)}
                    </select>
                  </label>
                  {isWrite ? (
                    <label className="block">
                      <span className="block text-xs font-medium text-foreground-600 mb-1">Paramètres JSON (écriture via Action Layer)</span>
                      <textarea
                        className={`${INPUT} resize-none font-mono text-xs`}
                        rows={3}
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        placeholder={selected === 'create_booking' ? '{"property_id":12,"guest_name":"Ahmed","check_in":"2026-11-01","check_out":"2026-11-05","guests":2}' : selected === 'create_invoice' ? '{"customer_name":"Ahmed","amount":1500,"currency":"MAD"}' : '{"fullname":"Ahmed","email":"ahmed@mail.com","date":"2026-10-20","time":"10:00"}'}
                      ></textarea>
                    </label>
                  ) : (
                    <label className="block">
                      <span className="block text-xs font-medium text-foreground-600 mb-1">{needsCustomerId ? 'Identifiant client (customer_id)' : 'Recherche (q)'}</span>
                      <input className={INPUT} value={inputValue} onChange={(e) => setInputValue(e.target.value)} placeholder={needsCustomerId ? 'Ex : 12' : 'Ex : Ahmed'} />
                    </label>
                  )}
                </div>
                {isWrite && (
                  <p className="text-[11px] text-foreground-400 flex items-center gap-1.5">
                    <i className="ri-lock-2-line"></i>
                    Écriture : l’appel passe par l’Action Layer (autorisation + idempotence). Rejouer les mêmes paramètres ne crée pas de doublon.
                  </p>
                )}
                {current?.required_permission && (
                  <p className="text-[11px] text-foreground-400">
                    Autorisation requise : <span className="font-medium text-foreground-600">{current.required_permission}</span>
                    {has(current.required_permission) ? ' — vous y avez accès ✓' : ' — refusé pour votre rôle ✗'}
                  </p>
                )}
                <button onClick={run} disabled={running} className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2">
                  {running ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-flashlight-line"></i>} Invoquer
                </button>

                {runError && <div className="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-700">{runError}</div>}

                {result && (
                  <div className={`rounded-md border p-3 text-xs ${result.ok ? 'bg-primary-50 border-primary-200' : 'bg-red-50 border-red-200'}`}>
                    <p className={`font-semibold flex items-center gap-1.5 ${result.ok ? 'text-primary-800' : 'text-red-700'}`}>
                      <i className={result.ok ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'}></i>
                      {result.ok ? `${result.count} résultat(s)` : result.error}
                    </p>
                    {result.ok && result.data.length > 0 && (
                      <pre className="mt-2 max-h-60 overflow-auto bg-background-50 rounded p-2 text-[11px] text-foreground-700 whitespace-pre-wrap break-words">
                        {JSON.stringify(result.data.slice(0, 5), null, 2)}
                      </pre>
                    )}
                  </div>
                )}
              </>
              )}

              {mode === 'chain' && (
              <>
                <p className="text-[11px] text-foreground-500">
                  Enchaînez plusieurs outils : chaque résultat est réutilisable dans les étapes suivantes via{' '}
                  <code className="bg-background-100 px-1 py-0.5 rounded">{'{{alias.chemin}}'}</code> (ex : <code className="bg-background-100 px-1 py-0.5 rounded">{'{{customers.0.id}}'}</code>).
                </p>
                <div className="space-y-3">
                  {planRows.map((row, idx) => (
                    <div key={idx} className="rounded-lg border border-background-200/70 bg-background-100/40 p-3 space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full bg-background-100 flex items-center justify-center text-[11px] font-bold text-foreground-500 flex-shrink-0">{idx + 1}</span>
                        <select className={`${INPUT} !w-auto flex-1`} value={row.tool} onChange={(e) => updatePlanRow(idx, { tool: e.target.value })}>
                          {activeTools.map((t) => <option key={t.tool_key} value={t.tool_key}>{t.tool_key}</option>)}
                        </select>
                        <input className={`${INPUT} !w-32`} value={row.alias} onChange={(e) => updatePlanRow(idx, { alias: e.target.value })} placeholder="alias" />
                        {planRows.length > 1 && (
                          <button onClick={() => setPlanRows((prev) => prev.filter((_, i) => i !== idx))} className="w-7 h-7 flex items-center justify-center rounded-full text-foreground-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer flex-shrink-0"><i className="ri-delete-bin-line text-sm"></i></button>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <select className={`${INPUT} !w-auto`} value={row.ifPrevious} onChange={(e) => updatePlanRow(idx, { ifPrevious: e.target.value as AiPlanCondition })}>
                          <option value="always">Toujours</option>
                          <option value="has_results">Si résultats</option>
                          <option value="no_results">Si aucun résultat</option>
                        </select>
                        <input className={`${INPUT} flex-1 font-mono text-xs`} value={row.paramsText} onChange={(e) => updatePlanRow(idx, { paramsText: e.target.value })} placeholder='{"q":"Ahmed"}' />
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setPlanRows((prev) => [...prev, newPlanRow('search_products', '', '{"q":""}')])} className="px-3 py-1.5 rounded-full bg-background-100 text-foreground-700 text-xs font-medium whitespace-nowrap hover:bg-background-200/70 transition-colors cursor-pointer"><i className="ri-add-line"></i> Ajouter une étape</button>
                  <button onClick={runChain} disabled={planRunning} className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2 ml-auto">
                    {planRunning ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-flow-chart"></i>} Enchaîner
                  </button>
                </div>

                {planError && <div className="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-700">{planError}</div>}

                {planRun && (
                  <div className="space-y-2">
                    {planRun.steps.map((s) => (
                      <div key={s.index} className={`rounded-md border p-3 text-xs ${s.ok ? 'bg-primary-50 border-primary-200' : 'bg-background-50 border-background-200/70'}`}>
                        <p className="font-semibold flex items-center gap-1.5 text-foreground-800">
                          <i className={s.ok ? 'ri-checkbox-circle-line text-primary-600' : 'ri-forbid-2-line text-foreground-400'}></i>
                          {s.index + 1}. {s.tool}
                          <span className="text-foreground-400 font-normal">→ {s.ok ? `${s.count} résultat(s)` : (s.error || 'ignorée')}</span>
                        </p>
                        {s.ok && s.data.length > 0 && (
                          <pre className="mt-1.5 max-h-40 overflow-auto bg-background-50 rounded p-2 text-[11px] text-foreground-700 whitespace-pre-wrap break-words">
                            {JSON.stringify(s.data.slice(0, 3), null, 2)}
                          </pre>
                        )}
                      </div>
                    ))}
                    {planRun.stoppedAt != null && (
                      <p className="text-[11px] text-red-600">Enchaînement interrompu à l’étape {planRun.stoppedAt + 1} : {planRun.error}</p>
                    )}
                  </div>
                )}
              </>
              )}
            </div>
          </div>

          {/* Journal */}
          <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
              <span className="w-8 h-8 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center"><i className="ri-history-line"></i></span>
              <h4 className="text-sm font-semibold text-foreground-900">Journal des appels IA</h4>
            </div>
            {calls.length === 0 ? (
              <p className="px-4 py-6 text-xs text-foreground-400 text-center">Aucun appel enregistré. Lancez un test ci-dessus.</p>
            ) : (
              <div className="divide-y divide-background-200/60 max-h-[420px] overflow-y-auto">
                {calls.map((c) => {
                  const meta = toolCallStatusMeta(c.status);
                  return (
                    <div key={c.id} className="flex items-center gap-3 px-4 py-2.5">
                      <i className="ri-robot-2-line text-foreground-500"></i>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold text-foreground-800 truncate">{c.tool_key}</p>
                        <p className="text-[11px] text-foreground-400 truncate">
                          {c.input ? JSON.stringify(c.input) : '—'}
                          {c.error ? ` · ${c.error}` : ''}
                          {c.created_at ? ` · ${new Date(c.created_at).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}` : ''}
                        </p>
                      </div>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${meta.className}`}>{meta.label}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}